import CheckoutContinuation, { useCheckoutNavigation } from '../common/CheckoutContinuation';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import BillingRecoveryPanel from '../Billing/BillingRecoveryPanel';
import { BILLING_RECOVERY_COPY, billingRecoveryCopy } from '../../lib/billingRecoveryCopy';
import { detectLang } from '../../hooks/useLanguage';
import { isCurrentUserBillingRecoveryRequired, useBillingRecoveryAction } from '../../hooks/useBillingRecoveryAction';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useAuthStore } from '../../stores/authStore';
import { useUIModalStore } from '../../stores/uiModalStore';
import { useCreditStore, CREDIT_PACKS } from '../../stores/creditStore';
import { NEURAL_HQ_SECONDS_PER_CREDIT, FREE_APPLY_USES_PER_DAY_ANON, FREE_APPLY_USES_PER_DAY_REGISTERED } from '../../lib/autotuneQuota';
import { HQSTEMS_FULL_CREDITS } from '../../lib/hqstemsLimits';
import { PRO_MONTHLY_CREDITS } from '../../lib/aiCreditsLimits';
import { trackEvent } from '../../lib/analytics';
import { usePricingQuote } from '../../hooks/usePricingQuote';
import { annualSavings, formatQuoteAmount, formatRecurringQuote, subscriptionCheckoutCopy } from '../../lib/pricingDisplay';
import { prefersZhPayment, formatCnyHint, PRO_PASS_PRICE_JPY, DAY_PASS_PRICE_JPY } from '../../lib/paymentLocale';
import { getPrimaryPricingOffer } from '../../lib/pricingOfferPolicy';
import './PricingModal.css';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalLang = 'ja' | 'en' | 'zh';

const FREE_APPLY_COPY = {
  ja: `未登録は1日${FREE_APPLY_USES_PER_DAY_ANON}回、無料アカウントは1日${FREE_APPLY_USES_PER_DAY_REGISTERED}回`,
  en: `${FREE_APPLY_USES_PER_DAY_ANON}/day without an account, ${FREE_APPLY_USES_PER_DAY_REGISTERED}/day with a free account`,
  zh: `未注册每天${FREE_APPLY_USES_PER_DAY_ANON}次，免费账号每天${FREE_APPLY_USES_PER_DAY_REGISTERED}次`,
};

// The modal renders on pages with and without the [lang] URL prefix (e.g.
// /editor). The editor (internal) route has NO LanguageProvider and NO [lang]
// segment, so the ONLY reliable signal there is the stored la-studio-lang
// preference — reading it FIRST (like the app-wide detectLang()) is what fixes
// the "renders all-Japanese despite la-studio-lang=en" paywall bug. Falls back
// to the URL locale (public [lang] pages) then the browser language.
// Most paying-intent traffic is EN/ZH — the old all-Japanese modal was a
// conversion killer for them.
function detectModalLang(): ModalLang {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = localStorage.getItem('la-studio-lang');
    if (stored === 'ja') return 'ja';
    if (stored === 'zh') return 'zh';
    if (stored) return 'en'; // any other supported locale → English modal copy
  } catch { /* private mode: fall through to path/nav */ }
  const seg = window.location.pathname.split('/')[1];
  if (seg === 'ja') return 'ja';
  if (seg === 'zh') return 'zh';
  if (['en', 'es', 'de', 'fr', 'pt', 'ko'].includes(seg)) return 'en';
  const nav = navigator.language || '';
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('zh')) return 'zh';
  return 'en';
}

// Recovery uses the banner's full locale set. Explicit public-page locale
// wins; storage may be denied in embedded/private browsing contexts.
function detectRecoveryLang(): string {
  if (typeof window === 'undefined') return 'en';
  const pathLang = window.location.pathname.split('/')[1];
  if (Object.prototype.hasOwnProperty.call(BILLING_RECOVERY_COPY, pathLang)) return pathLang;
  try { return detectLang(); } catch {
    const browserLang = (navigator.language || '').split('-')[0].toLowerCase();
    return Object.prototype.hasOwnProperty.call(BILLING_RECOVERY_COPY, browserLang) ? browserLang : 'en';
  }
}

// Currency follows the RESOLVED modal language, not navigator alone: with
// la-studio-lang=en the modal is English, so it must open on USD ($) — even on
// a JP browser. JPY only when the modal itself is Japanese.
function detectDefaultCurrency(): 'jpy' | 'usd' {
  // zh-payment visitors: WeChat Pay / Alipay only exist on JPY one-time
  // sessions (USD credit-pack sessions are card+link only: 14 opens, 0
  // completions 07-07〜09-05), so the packs must open on JPY for them.
  const lang = detectModalLang();
  if (lang === 'ja' || prefersZhPayment(lang)) return 'jpy';
  return 'usd';
}

interface SourceCopy {
  title: string;
  subtitle: string;
  cta: (price: string) => string;
  highlights: string[];
}

// The Pro feature list is composed from these verified, enforced benefits
// (see PLAN_CONFIG, autotuneQuota.ts, loopforgeLimits.ts, hqstemsLimits.ts —
// no unverified/"trust us" bullets like ads or priority support). Which one
// leads depends on why the modal was opened (see PRO_FEATURE_ORDER below).
type ProFeatureKey = 'hq' | 'harmony' | 'preview' | 'export' | 'loopforge' | 'noise' | 'hqstems' | 'credits';

// Scannable value clusters for the Pro feature list — same 7 verified
// benefits as ProFeatureKey, grouped so the bundle's breadth reads at a
// glance instead of as one flat 7-line list. Membership is fixed; the
// per-source LEAD cluster still comes from PRO_FEATURE_ORDER below (see
// getOrderedFeatureClusters), so autotune/loopforge/hqstems upsells keep
// surfacing their own cluster first.
type FeatureCluster = 'vocals' | 'separation' | 'create' | 'master';

interface ModalStrings {
  free: string;
  currentPlan: string;
  redirecting: string;
  checkoutFailed: string;
  trialCta: string;
  trialNote: (price: string) => string;
  // Bundle-value reframe shown above the Pro pitch (modal body + Pro card):
  // leads with "one plan, N tools" instead of any single feature, then
  // anchors against real competitor prices. Both take the resolved price
  // label (e.g. "$5/mo") so they stay correct across the currency toggle.
  bundleHeader: (price: string) => string;
  bundleAnchor: (price: string) => string;
  clusterLabels: Record<FeatureCluster, string>;
  // Pro 30-Day Pass (¥980 one-time, mode=payment so WeChat Pay is available).
  // zh: primary CTA (subscriptions are card-only — a dead end for most
  // Chinese buyers). ja/en: small secondary option below the trial CTA.
  passCta: string;
  passNote: string;
  // 24-Hour Pass (¥200 one-time, same WeChat-capable payment mode). Checkout
  // metadata showed ~90% of paywall intent is "finish this one song today" —
  // this is the price shaped for that moment. Leads on quota walls.
  dayPassCta: string;
  dayPassNote: string;
  // Money-back guarantee line under the CTA area (churn data shows honest
  // framing converts better than urgency for this audience).
  refundNote: string;
  // Pre-checkout email capture for anonymous buyers: turns the 92% of
  // checkout-openers we could never contact again into recoverable leads,
  // and pre-fills Stripe's email field (net-neutral friction).
  emailGateTitle: string;
  emailGateNote: string;
  emailGateContinue: string;
  emailGateBack: string;
  emailGateInvalid: string;
  // zh only: card-only caveat appended to the demoted subscription option.
  subscriptionCardOnly?: string;
  creditPacksTitle: string;
  creditPacksNote: string;
  creditsUnit: string;
  buyCredits: string;
  // ja/en: secondary "buy credits instead" note shown below the subscription
  // CTA — combines the no-subscription pitch with a concrete conversion
  // (1 credit = 1 minute of AI Natural HQ, see NEURAL_HQ_SECONDS_PER_CREDIT).
  creditPacksSecondaryNote: string;
  creditPacksMinutesLabel: (minutes: number) => string;
  perMonth: { jpy: string; usd: string };
  // Annual billing (¥3,600/yr / $36/yr — 40% off vs. paying monthly).
  perYear: { jpy: string; usd: string };
  billingMonthly: string;
  billingAnnual: string;
  annualBadge: string;
  // EARLYBIRD promo surface: the coupon existed since 2026-07-02 but
  // earlybird_shown stayed at 0 — it only appeared on quota exhaustion,
  // which nobody hits. Now shown on the 2nd+ paywall open (repeat interest).
  earlybirdTitle: string;
  earlybirdBody: string;
  oneTimeOptions: string;
  renewalNote: string;
  // One-shot intent upsell: small muted line on the Pro card nudging
  // day-pass-primary users toward the subscription if they'll be back.
  proContinuityNote: string;
  // PPP regional pricing: the coupon auto-applies server-side at checkout,
  // so this footnote just sets expectations under the currency toggle.
  pppFootnote: string;
  freeFeatures: { text: string; disabled?: boolean }[];
  proFeatureText: Record<ProFeatureKey, string>;
  copy: Record<SourceCategory, SourceCopy>;
}

const STRINGS: Record<ModalLang, ModalStrings> = {
  ja: {
    free: 'Free',
    currentPlan: '現在のプラン',
    redirecting: 'リダイレクト中...',
    checkoutFailed: 'チェックアウトの作成に失敗しました。時間をおいて再度お試しください。',
    trialCta: '7日間無料で試す',
    trialNote: (p) => `カードの登録が必要ですが、7日間は請求されません。期間内の解約で費用は一切かかりません（継続は${p}）。`,
    passCta: 'サブスク不要のPro 30日パス ¥980',
    passNote: '一回払い・自動更新なし。30日間Proのすべての機能が使えます。',
    dayPassCta: 'この曲だけ仕上げる — 24時間パス ¥200',
    refundNote: '返金は原則不可です。お支払い後7日以内で利用が少ない場合は、サポートに返金をご相談いただけます。返金を保証するものではありません。',
    emailGateTitle: '購入内容の受け取りに使うメールアドレス',
    emailGateNote: '領収書と購入内容がこのアドレスに届きます。決済ページにも自動入力されます。',
    emailGateContinue: '決済に進む',
    emailGateBack: '戻る',
    emailGateInvalid: 'メールアドレスの形式が正しくありません。',
    dayPassNote: '一回払い・自動更新なし。24時間Proのすべての機能が使えます。',
    creditPacksTitle: 'クレジットパック（買い切り）',
    creditPacksNote: 'サブスク不要の一回払い。',
    creditsUnit: 'クレジット',
    buyCredits: '購入する',
    creditPacksSecondaryNote: 'サブスク不要の買い切りクレジットという選択肢もあります。',
    creditPacksMinutesLabel: (_m) => `HQボーカル分離 ${HQSTEMS_FULL_CREDITS}クレジット/曲`,
    perMonth: { jpy: '¥500/月', usd: '$5/mo' },
    perYear: { jpy: '¥3,600/年', usd: '$36/yr' },
    billingMonthly: '月払い',
    billingAnnual: '年払い',
    annualBadge: '40%オフ',
    earlybirdTitle: 'クーポン: 最初の3ヶ月が50%オフ',
    earlybirdBody: 'チェックアウトで自動的に割引が適用されます。',
    oneTimeOptions: '一回払いのパスも選べます（自動更新なし）',
    renewalNote: '解約するまで自動更新されます。適用される割引と請求額は決済画面で確認できます。',
    proContinuityNote: '明日も使うなら Pro がお得 — 7日間無料',
    pppFootnote: 'お住まいの地域によっては 40% 割引が自動適用されます',
    bundleHeader: (p) => p ? `Pro機能をひとつのプランで — ${p}` : 'Pro機能をひとつのプランで',
    bundleAnchor: (_p) => 'オートチューン適用は無制限。HQボーカル分離は1日15回、Loop Forgeは1日100回。ハーモニーと96kHz書き出しも利用できます。',
    clusterLabels: { vocals: 'ボーカル', separation: '分離', create: '制作', master: '仕上げ' },
    freeFeatures: [
      { text: '全エフェクト・編集機能' },
      { text: 'ボーカル除去 / ステム分離' },
      { text: 'ノイズ除去 / BPM検出' },
      { text: `オートチューン（ノート編集 / Lyra HQ・${FREE_APPLY_COPY.ja}）` },
      { text: 'MIDI / ピアノロール' },
      { text: 'WAV エクスポート (44.1kHz / 24bit)' },
      { text: 'クラウドプロジェクト保存（5件まで / 合計300MB）' },
      { text: 'AIチャット（ログイン必須 / 1日10回まで）' },
      { text: 'サンプルストア（試聴のみ）', disabled: true },
      { text: '高品質エクスポート (48kHz+ / 32bit)', disabled: true },
    ],
    proFeatureText: {
      hq: `オートチューン適用無制限（無料枠：${FREE_APPLY_COPY.ja}）`,
      harmony: 'AIハモリ回数制限なし（無料は1日1回）',
      preview: 'クラシックハーモニーの全曲書き出し（試聴は無料）',
      export: '96kHz・32bit float 書き出し',
      loopforge: 'Loop Forge 100回/日 生成 + バッチ生成',
      noise: 'バッチノイズ除去',
      hqstems: 'HQボーカル分離（クラウドAI・スタジオ品質）',
      credits: `AI音楽生成クレジット 毎月${PRO_MONTHLY_CREDITS}枚（¥500のクレジットパックと同額）`,
    },
    copy: {
      vocal: {
        title: '音源はできた — 次はこの1曲を仕上げる',
        subtitle: '作ったカラオケ音源に歌を重ねて、音程補正・ハーモニー・マスタリングまでこのブラウザで完結。外注すれば1曲数千円のMIXを、自分の手で仕上げられます。',
        cta: (p) => `この1曲を仕上げる (${p})`,
        highlights: [`オートチューン適用無制限（無料枠：${FREE_APPLY_COPY.ja}）`, 'AIハモリ回数制限なし', '96kHz高品質書き出しとクラウド保存'],
      },
      stems: {
        title: '分離したステムで、そのままリミックス',
        subtitle: 'ボーカル・ドラム・ベースを並べてMIXし直し、高音質で書き出せます。もっとクリーンな分離が必要なら、スタジオ品質のHQボーカル分離もProで使えます。',
        cta: (p) => `Proで続ける (${p})`,
        highlights: ['HQボーカル分離（クラウドAI・スタジオ品質）', 'ステムのクラウド保存とZIP一括書き出し', '96kHz高品質エクスポート'],
      },
      hqstems: {
        title: 'スタジオ品質のHQボーカル分離',
        subtitle: 'クラウドGPUで曲をクリーンなボーカルと伴奏に分離します。無料のオンデバイス6ステムモデルより明確に高品質です。Proは1日15回、またはクレジット5枚/曲で利用できます。',
        cta: (p) => `ProでHQボーカル分離を使う (${p})`,
        highlights: ['スタジオ品質のボーカル/伴奏分離', 'Proは1日15回のHQ分離', '毎回30秒の無料プレビュー'],
      },
      export: {
        title: 'このプロジェクトを書き出す',
        subtitle: '48kHz/32bit floatなどの高品質書き出しと、トラック別ZIP書き出しを使えます。',
        cta: (p) => `高品質で書き出す (${p})`,
        highlights: ['48kHz+ / 32bit float', 'トラック別ZIP書き出し', '書き出し結果の保存'],
      },
      ocr: {
        title: '楽譜OCRを続けて使う',
        subtitle: '楽譜の読み取り、MIDI化、プロジェクト保存を継続して使えます。',
        cta: (p) => `楽譜OCRを続ける (${p})`,
        highlights: ['楽譜OCR無制限', 'MIDI/プロジェクト保存', 'AIチャット無制限'],
      },
      classic_harmony: {
        title: 'ハーモニーを曲全体に',
        subtitle: '無料で試聴したオクターブ・5度のクラシックハーモニーを、Proで曲全体に適用して書き出せます。',
        cta: (p) => `Proで曲全体を仕上げる (${p})`,
        highlights: ['クラシックハーモニーの全曲書き出し', 'オートチューン適用無制限', '今の音源と編集内容を引き継ぐ'],
      },
      autotune: {
        title: 'オートチューンを続ける',
        subtitle: `ノート編集つきのオートチューンを無制限で。無料枠は${FREE_APPLY_COPY.ja}。Proなら適用回数が無制限です。`,
        cta: (p) => `Proで無制限にする (${p})`,
        highlights: ['適用回数無制限', 'AIハモリ回数制限なし', '96kHz・32bit float 書き出し'],
      },
      loopforge: {
        title: 'Loop Forgeを続けて使う',
        subtitle: 'AIループ生成はProなら1日100回まで、バッチ生成（連続実行）にも対応します。無料は1日5回までです。',
        cta: (p) => `ProでLoop Forgeを使う (${p})`,
        highlights: ['1日100回まで生成', 'バッチ生成（連続実行）', 'オートチューン適用も無制限'],
      },
      musicgen: {
        title: 'AI楽曲生成を続ける',
        subtitle: `Proなら毎月${PRO_MONTHLY_CREDITS}クレジットが付いて、テキストからの生成・カバー・リペイントをそのまま続けられます。無料の登録ボーナス30枚を使い切ってもここで止まりません。`,
        cta: (p) => `Proで生成を続ける (${p})`,
        highlights: [`AI生成クレジット 毎月${PRO_MONTHLY_CREDITS}枚`, 'Loop Forge 1日100回 + バッチ生成', 'HQボーカル分離もPro枠で'],
      },
      default: {
        title: 'この曲を最後まで仕上げる',
        subtitle: '主要ツールは無料のまま。Proは音程補正の無制限化、ハーモニー、HQボーカル分離、96kHz書き出しなど「仕上げ」を強化します。',
        cta: (p) => `Proにアップグレード (${p})`,
        highlights: ['オートチューン無制限＋ハーモニー', 'HQボーカル分離（クラウドAI）', '96kHz書き出しとクラウド保存'],
      },
    },
  },
  en: {
    free: 'Free',
    currentPlan: 'Current plan',
    redirecting: 'Redirecting…',
    checkoutFailed: 'Could not start checkout. Please try again in a moment.',
    trialCta: 'Try free for 7 days',
    trialNote: (p) => `Card required to start, but you won't be charged during the trial. Cancel anytime within 7 days and you pay nothing (${p} after).`,
    passCta: 'Pro 30-day pass ¥980 — one-time',
    passNote: 'One payment, no auto-renewal. Full Pro for 30 days.',
    dayPassCta: 'Finish this song — 24-hour pass ¥200 (≈$1.40)',
    refundNote: 'Refunds are generally unavailable. If usage is limited, contact support within 7 days of payment for a refund review. A refund is not guaranteed.',
    emailGateTitle: 'Where should we send your receipt?',
    emailGateNote: 'Your receipt and purchase details go here — it also pre-fills the payment page.',
    emailGateContinue: 'Continue to payment',
    emailGateBack: 'Back',
    emailGateInvalid: 'That email address does not look right.',
    dayPassNote: 'One payment, no auto-renewal. Full Pro for 24 hours.',
    creditPacksTitle: 'Credit packs (one-time)',
    creditPacksNote: 'One-time payment, no subscription.',
    creditsUnit: 'credits',
    buyCredits: 'Buy',
    creditPacksSecondaryNote: 'One-time credits, no subscription, is also an option.',
    creditPacksMinutesLabel: (_m) => `${HQSTEMS_FULL_CREDITS} credits per HQ vocal separation`,
    perMonth: { jpy: '¥500/mo', usd: '$5/mo' },
    perYear: { jpy: '¥3,600/yr', usd: '$36/yr' },
    billingMonthly: 'Monthly',
    billingAnnual: 'Annual',
    annualBadge: '40% off',
    earlybirdTitle: 'Coupon: 50% off your first 3 months',
    earlybirdBody: 'The discount is applied automatically at checkout.',
    oneTimeOptions: 'One-time passes (no subscription)',
    renewalNote: 'Renews automatically until canceled. Review discounts and the amount due at checkout.',
    proContinuityNote: 'Coming back tomorrow? Pro pays for itself — 7-day free trial',
    pppFootnote: 'A 40% regional discount is applied automatically in select countries.',
    bundleHeader: (p) => p ? `Pro tools in one plan — ${p}` : 'Pro tools in one plan',
    bundleAnchor: (_p) => 'Unlimited autotune applies, 15 HQ vocal separations per day, and 100 Loop Forge generations per day. Harmony and 96 kHz export are included too.',
    clusterLabels: { vocals: 'Vocals', separation: 'Separation', create: 'Create', master: 'Master' },
    freeFeatures: [
      { text: 'All effects & editing tools' },
      { text: 'Vocal remover / stem separation' },
      { text: 'Noise removal / BPM detection' },
      { text: `Autotune (note editing / Lyra HQ; ${FREE_APPLY_COPY.en})` },
      { text: 'MIDI / piano roll' },
      { text: 'WAV export (44.1 kHz / 24-bit)' },
      { text: 'Cloud project saves (up to 5 / 300 MB)' },
      { text: 'AI chat (login required / 10 per day)' },
      { text: 'Sample store (preview only)', disabled: true },
      { text: 'High-quality export (48 kHz+ / 32-bit)', disabled: true },
    ],
    proFeatureText: {
      hq: `Unlimited autotune applies (Free: ${FREE_APPLY_COPY.en})`,
      harmony: 'AI vocal harmony with no daily limit (free: 1/day)',
      preview: 'Full-song classic harmony exports (free previews)',
      export: '96 kHz / 32-bit float export',
      loopforge: 'Loop Forge: 100 generations/day + batch mode',
      noise: 'Batch noise removal',
      hqstems: 'HQ Vocal Separation (cloud AI, studio-grade)',
      credits: `${PRO_MONTHLY_CREDITS} AI music-generation credits every month (a $5 pack's worth)`,
    },
    copy: {
      vocal: {
        title: 'Save your vocal-removal results in high quality',
        subtitle: 'Keep your karaoke tracks and acapellas in the cloud, re-edit them anytime, and export in high quality.',
        cta: (p) => `Save in high quality (${p})`,
        highlights: ['High-quality WAV export', 'Cloud project saves', 'Re-edit results anytime'],
      },
      stems: {
        title: 'Save all your separated stems',
        subtitle: 'Store vocals, drums, bass and other stems as a project and come back to edit later.',
        cta: (p) => `Save my stems (${p})`,
        highlights: ['Cloud stem storage', 'One-click ZIP export', 'High-quality export'],
      },
      hqstems: {
        title: 'Studio-grade HQ Vocal Separation',
        subtitle: 'Split any song into clean vocals and instrumental on a cloud GPU — noticeably cleaner than the free on-device 6-stem model. Pro gets 15 separations a day, or pay 5 credits per song.',
        cta: (p) => `Get HQ Vocal Separation with Pro (${p})`,
        highlights: ['Studio-grade vocal + instrumental', '15 HQ separations/day on Pro', 'Free 30-second preview on every song'],
      },
      export: {
        title: 'Export this project',
        subtitle: 'Unlock 48 kHz / 32-bit float export and per-track ZIP export.',
        cta: (p) => `Export in high quality (${p})`,
        highlights: ['48 kHz+ / 32-bit float', 'Per-track ZIP export', 'Saved export history'],
      },
      ocr: {
        title: 'Keep using Sheet Music OCR',
        subtitle: 'Unlimited sheet-music scanning, MIDI conversion, and project saves.',
        cta: (p) => `Continue with OCR (${p})`,
        highlights: ['Unlimited sheet-music OCR', 'MIDI / project saves', 'Unlimited AI chat'],
      },
      classic_harmony: {
        title: 'Bring harmony to the whole song',
        subtitle: 'Preview classic octave and fifth harmonies for free, then apply and export the full song with Pro.',
        cta: (p) => `Finish the full song with Pro (${p})`,
        highlights: ['Full-song classic harmony exports', 'Unlimited autotune applies', 'Keep your audio and note edits'],
      },
      autotune: {
        title: 'Keep tuning without limits',
        subtitle: `Unlimited note-level autotune applies. Free allowance: ${FREE_APPLY_COPY.en}.`,
        cta: (p) => `Go unlimited with Pro (${p})`,
        highlights: ['Unlimited applies', 'AI harmony with no daily limit', '96 kHz / 32-bit float export'],
      },
      loopforge: {
        title: 'Continue with Loop Forge',
        subtitle: 'Pro unlocks 100 AI loop generations a day plus batch (run-forever) mode. Free is limited to 5 a day.',
        cta: (p) => `Get Loop Forge with Pro (${p})`,
        highlights: ['100 generations/day', 'Batch (run-forever) mode', 'Unlimited autotune applies too'],
      },
      musicgen: {
        title: 'Keep generating with AI',
        subtitle: `Pro includes ${PRO_MONTHLY_CREDITS} AI credits every month, so text-to-music, covers and repaints keep working after the 30-credit signup bonus runs out.`,
        cta: (p) => `Keep generating with Pro (${p})`,
        highlights: [`${PRO_MONTHLY_CREDITS} AI generation credits a month`, 'Loop Forge: 100/day + batch mode', 'HQ vocal separation included too'],
      },
      default: {
        title: 'Save your work and re-edit anytime',
        subtitle: 'The core tools stay free. Pro adds cloud saves, high-quality export, and unlimited autotune.',
        cta: (p) => `Upgrade to Pro (${p})`,
        highlights: ['Cloud project saves', 'High-quality export', 'Unlimited autotune applies'],
      },
    },
  },
  zh: {
    free: 'Free',
    currentPlan: '当前方案',
    redirecting: '跳转中…',
    checkoutFailed: '无法创建结账页面，请稍后重试。',
    trialCta: '免费试用7天',
    trialNote: (p) => `试用期内不会扣款，但开始前需要绑定银行卡。7天内随时取消，不收取任何费用（之后为 ${p}）。`,
    passCta: 'Pro 30天通行证 — ¥980（约30天）',
    passNote: '一次性付款 · 支持微信支付/支付宝 · 无需信用卡',
    dayPassCta: '只想做完这首歌 — 24小时通行证 ¥200',
    refundNote: '原则上不退款。如付款后7天内使用次数较少，可联系客服申请退款审核，但不保证退款。',
    emailGateTitle: '接收凭证的邮箱地址',
    emailGateNote: '收据和购买信息将发送到此邮箱，并自动填入支付页面。',
    emailGateContinue: '前往支付',
    emailGateBack: '返回',
    emailGateInvalid: '邮箱格式不正确。',
    dayPassNote: '一次性付款 · 支持微信支付/支付宝 · 24小时内使用全部 Pro 功能',
    subscriptionCardOnly: '订阅仅支持国际信用卡（Visa/Mastercard 等），不支持微信支付/支付宝。',
    creditPacksTitle: '积分包（一次性购买）',
    creditPacksNote: '一次性付款，无需订阅。支持微信支付/支付宝。',
    creditsUnit: '积分',
    buyCredits: '购买',
    creditPacksSecondaryNote: '一次性积分，无需订阅。',
    creditPacksMinutesLabel: (_m) => `HQ 人声分离每首 ${HQSTEMS_FULL_CREDITS} 积分`,
    perMonth: { jpy: '¥500/月', usd: '$5/月' },
    perYear: { jpy: '¥3,600/年', usd: '$36/年' },
    billingMonthly: '按月付',
    billingAnnual: '按年付',
    annualBadge: '省40%',
    earlybirdTitle: '优惠码：前3个月半价',
    earlybirdBody: '折扣将在结账时自动应用。',
    oneTimeOptions: '也可选择一次性通行证（不自动续费）',
    renewalNote: '取消前将自动续费。适用折扣和应付金额请在结账页面确认。',
    proContinuityNote: '明天还要用？Pro 更划算 — 免费试用7天',
    pppFootnote: '部分国家/地区将自动享受 40% 的地区折扣。',
    bundleHeader: (p) => p ? `一个方案，多种 Pro 工具 — ${p}` : '一个方案，多种 Pro 工具',
    bundleAnchor: (_p) => '修音次数不限，HQ 人声分离每天15次，Loop Forge 每天100次。还可使用和声和96kHz导出。',
    clusterLabels: { vocals: '人声', separation: '分离', create: '创作', master: '输出' },
    freeFeatures: [
      { text: '全部效果器与编辑功能' },
      { text: '人声分离 / 多轨分离' },
      { text: '降噪 / BPM 检测' },
      { text: `Autotune（音符编辑 / Lyra HQ；${FREE_APPLY_COPY.zh}）` },
      { text: 'MIDI / 钢琴卷帘' },
      { text: 'WAV 导出 (44.1kHz / 24bit)' },
      { text: '云端项目保存（最多5个 / 共300MB）' },
      { text: 'AI 聊天（需登录 / 每天10次）' },
      { text: '采样商店（仅试听）', disabled: true },
      { text: '高品质导出 (48kHz+ / 32bit)', disabled: true },
    ],
    proFeatureText: {
      hq: `修音应用次数不限（免费额度：${FREE_APPLY_COPY.zh}）`,
      harmony: 'AI 和声不限次数（免费每天 1 次）',
      preview: '经典和声整曲导出（免费试听）',
      export: '96kHz・32bit float 导出',
      loopforge: 'Loop Forge 每天100次生成 + 批量生成',
      noise: '批量降噪',
      hqstems: 'HQ 人声分离（云端 AI · 录音室级）',
      credits: `每月赠送 ${PRO_MONTHLY_CREDITS} 个 AI 音乐生成积分（相当于一个入门积分包）`,
    },
    copy: {
      vocal: {
        title: '以高品质保存人声分离结果',
        subtitle: '将制作的伴奏和阿卡贝拉保存到云端，随时重新编辑并高品质导出。',
        cta: (p) => `高品质保存 (${p})`,
        highlights: ['高品质 WAV 导出', '云端项目保存', '随时重新编辑'],
      },
      stems: {
        title: '一键保存所有分离音轨',
        subtitle: '将人声、鼓、贝斯等分离结果保存为项目，之后可继续编辑。',
        cta: (p) => `保存我的音轨 (${p})`,
        highlights: ['音轨云端保存', 'ZIP 打包导出', '高品质导出'],
      },
      hqstems: {
        title: '录音室级 HQ 人声分离',
        subtitle: '在云端 GPU 上把歌曲分离成干净的人声与伴奏，比免费的本地 6 轨模型明显更干净。Pro 每天 15 次，或每首 5 积分。',
        cta: (p) => `用 Pro 解锁 HQ 人声分离 (${p})`,
        highlights: ['录音室级人声/伴奏分离', 'Pro 每天 15 次 HQ 分离', '每首歌免费 30 秒预览'],
      },
      export: {
        title: '导出这个项目',
        subtitle: '解锁 48kHz / 32bit float 高品质导出和分轨 ZIP 导出。',
        cta: (p) => `高品质导出 (${p})`,
        highlights: ['48kHz+ / 32bit float', '分轨 ZIP 导出', '导出记录保存'],
      },
      ocr: {
        title: '继续使用乐谱 OCR',
        subtitle: '无限次乐谱识别、MIDI 转换和项目保存。',
        cta: (p) => `继续使用 OCR (${p})`,
        highlights: ['乐谱 OCR 无限使用', 'MIDI / 项目保存', 'AI 聊天无限使用'],
      },
      classic_harmony: {
        title: '为整首歌添加和声',
        subtitle: '免费试听八度和五度经典和声，使用 Pro 应用并导出整首歌曲。',
        cta: (p) => `用 Pro 完成整首歌 (${p})`,
        highlights: ['经典和声整曲导出', '无限次修音应用', '保留音频和音符编辑'],
      },
      autotune: {
        title: '继续修音，不再受限',
        subtitle: `音符级修音应用次数不限。免费额度为${FREE_APPLY_COPY.zh}，Pro 无次数限制。`,
        cta: (p) => `用 Pro 解锁无限修音 (${p})`,
        highlights: ['应用次数无限', 'AI 和声不限次数', '96kHz / 32bit float 导出'],
      },
      loopforge: {
        title: '继续使用 Loop Forge',
        subtitle: 'Pro 每天可生成 100 次 AI 循环，并支持批量（连续）生成。免费版每天限 5 次。',
        cta: (p) => `用 Pro 解锁 Loop Forge (${p})`,
        highlights: ['每天100次生成', '批量（连续）生成', '修音应用也无限使用'],
      },
      musicgen: {
        title: '继续使用 AI 音乐生成',
        subtitle: `Pro 每月赠送 ${PRO_MONTHLY_CREDITS} 个积分，注册赠送的 30 个用完后，文本生成、翻唱和重绘都能继续使用。`,
        cta: (p) => `用 Pro 继续生成 (${p})`,
        highlights: [`每月 ${PRO_MONTHLY_CREDITS} 个 AI 生成积分`, 'Loop Forge 每天100次 + 批量生成', 'HQ 人声分离同样包含'],
      },
      default: {
        title: '保存你的作品，随时继续编辑',
        subtitle: '核心工具保持免费。Pro 增加云端保存、高品质导出和无限修音。',
        cta: (p) => `升级到 Pro (${p})`,
        highlights: ['云端项目保存', '高品质导出', '修音无限使用'],
      },
    },
  },
};

type SourceCategory = 'vocal' | 'stems' | 'hqstems' | 'export' | 'ocr' | 'autotune' | 'classic_harmony' | 'loopforge' | 'musicgen' | 'default';

// Single place that classifies an openPricingModal(source) string. Both the
// header copy (title/subtitle/highlights) and the Pro feature list order
// key off this so the two stay in sync without duplicating the detection
// logic. `source` can be a plain slug ('loopforge_quota') or a compound one
// from useProUpsellPrompt ('tool_outcome:autotune'), hence `.includes`.
function getSourceCategory(source: string): SourceCategory {
  const sourceKey = source.replace(/-/g, '_');
  if (sourceKey === 'autotune_harmony') return 'classic_harmony';
  // hqstems before stems: 'hqstems_*' sources lead with the cloud HQ bullet,
  // not the on-device 6-stem "save your stems" copy. 'stem_to_hq' is the free
  // standard-separator → HQ Vocal cross-sell, so it leads with HQ too.
  if (sourceKey.includes('hqstems') || sourceKey.includes('stem_to_hq')) return 'hqstems';
  if (sourceKey.includes('vocal_remover') || sourceKey.includes('karaoke')) return 'vocal';
  if (sourceKey.includes('stem_separator')) return 'stems';
  if (sourceKey.includes('export') || sourceKey.includes('hq_export')) return 'export';
  if (sourceKey.includes('sheet') || sourceKey.includes('ocr')) return 'ocr';
  if (sourceKey.includes('autotune') || sourceKey.includes('neural_hq') || sourceKey.includes('natural') || sourceKey.includes('harmony')) return 'autotune';
  if (sourceKey.includes('loopforge')) return 'loopforge';
  if (sourceKey.includes('musicgen') || sourceKey.includes('music_gen')) return 'musicgen';
  return 'default';
}

function getSourceCopy(strings: ModalStrings, category: SourceCategory): SourceCopy {
  return strings.copy[category];
}

// Which of the seven verified Pro benefits leads the feature list. Autotune
// entry points lead with HQ + harmony (the two the user was just gated on);
// Loop Forge entry points lead with Loop Forge itself; every other source
// (vocal/stems/export/ocr/default) uses one unchanged, balanced order —
// this is a small reorder, not per-source copy for every bullet.
const DEFAULT_PRO_FEATURE_ORDER: ProFeatureKey[] = ['hq', 'hqstems', 'credits', 'export', 'loopforge', 'harmony', 'preview', 'noise'];
const PRO_FEATURE_ORDER: Partial<Record<SourceCategory, ProFeatureKey[]>> = {
  classic_harmony: ['preview', 'hq', 'harmony', 'hqstems', 'credits', 'export', 'loopforge', 'noise'],
  autotune: ['hq', 'harmony', 'preview', 'hqstems', 'credits', 'export', 'loopforge', 'noise'],
  loopforge: ['loopforge', 'credits', 'hq', 'hqstems', 'export', 'harmony', 'preview', 'noise'],
  hqstems: ['hqstems', 'hq', 'credits', 'export', 'loopforge', 'harmony', 'preview', 'noise'],
  musicgen: ['credits', 'loopforge', 'hqstems', 'hq', 'export', 'harmony', 'preview', 'noise'],
};

// Fixed cluster membership for the bundle-value feature list (see
// FeatureCluster above) — deliberately NOT a per-source table. Cluster
// ORDER is derived below from the existing PRO_FEATURE_ORDER/
// DEFAULT_PRO_FEATURE_ORDER (untouched), so any source added there later
// keeps leading with its own cluster automatically, with no second table
// to keep in sync.
const FEATURE_CLUSTER_OF: Record<ProFeatureKey, FeatureCluster> = {
  hq: 'vocals',
  harmony: 'vocals',
  preview: 'vocals',
  hqstems: 'separation',
  loopforge: 'create',
  credits: 'create',
  export: 'master',
  noise: 'master',
};

interface OrderedFeatureCluster {
  id: FeatureCluster;
  label: string;
  features: string[];
}

function getOrderedFeatureClusters(strings: ModalStrings, category: SourceCategory): OrderedFeatureCluster[] {
  const order = PRO_FEATURE_ORDER[category] ?? DEFAULT_PRO_FEATURE_ORDER;
  const clusters: OrderedFeatureCluster[] = [];
  for (const key of order) {
    const id = FEATURE_CLUSTER_OF[key];
    let cluster = clusters.find((c) => c.id === id);
    if (!cluster) {
      cluster = { id, label: strings.clusterLabels[id], features: [] };
      clusters.push(cluster);
    }
    cluster.features.push(strings.proFeatureText[key]);
  }
  return clusters;
}

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
  const { pending: pendingCheckout, navigate: navigateCheckout, clear: clearCheckout } = useCheckoutNavigation();
  const [currency, setCurrency] = useState<'jpy' | 'usd'>(detectDefaultCurrency);
  const [lang] = useState<ModalLang>(detectModalLang);
  const [recoveryLang] = useState(detectRecoveryLang);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isPassRedirecting, setIsPassRedirecting] = useState(false);
  const [buyingPackId, setBuyingPackId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const pricingQuote = usePricingQuote(currency, isOpen);
  // EARLYBIRD surfaces on the 2nd+ paywall open: a repeat open is interest
  // that didn't convert, exactly who a launch discount is for. (localStorage
  // count survives sessions; the coupon itself is validated by Stripe.)
  const [showEarlybird, setShowEarlybird] = useState(false);
  const earlybirdFiredRef = useRef(false);
  const recovery = useBillingRecoveryAction(isOpen && !isRedirecting && !isPassRedirecting && buyingPackId === null, 'pricing_modal');
  const { needsRecovery, currentPlan: plan } = recovery;
  const createCheckoutSession = useSubscriptionStore((s) => s.createCheckoutSession);
  const createPassCheckout = useSubscriptionStore((s) => s.createPassCheckout);
  const buyCredits = useCreditStore((s) => s.buyCredits);
  const user = useAuthStore((s) => s.user);
  const source = useUIModalStore((s) => s.pricingModalSource);
  const ctaShownKeyRef = useRef('');
  const packsShownKeyRef = useRef('');
  const bundleViewFiredRef = useRef(false);

  const strings = STRINGS[lang];
  // The payment-block LAYOUT (which CTA leads: pass/packs vs. card-only
  // subscription) is keyed on the visitor's browser language, not the page
  // locale — a zh-browser visitor on an EN page still can't use a card-only
  // subscription. Page chrome (title/subtitle/highlights/feature list/plan
  // cards) stays keyed on `lang` (page locale) via `strings` below;
  // `paymentStrings` is only for the pass/packs/subscription-demotion copy.
  const zhPayment = prefersZhPayment(lang);
  const paymentStrings = zhPayment ? STRINGS.zh : strings;
  const priceLabel = formatRecurringQuote(pricingQuote?.monthly, lang, 'month');
  const annualLabel = formatRecurringQuote(pricingQuote?.yearly, lang, 'year');
  const savings = annualSavings(pricingQuote?.monthly, pricingQuote?.yearly);
  const annualBadge = savings === null ? null : lang === 'ja' ? `${savings}%オフ` : lang === 'zh' ? `省${savings}%` : `${savings}% off`;
  const dayPassLabel = pricingQuote?.dayPass ? formatQuoteAmount(pricingQuote.dayPass, zhPayment ? 'zh' : lang) : (lang === 'ja' ? '決済画面で料金を確認' : zhPayment ? '在结账页面确认价格' : 'Price confirmed at checkout');
  const dayPassCta = zhPayment ? `24小时通行证 ${dayPassLabel}` : lang === 'ja' ? `24時間パス ${dayPassLabel}` : `24-hour pass ${dayPassLabel}`;
  // Use the same resolved recurring price on the toggle, CTA and Pro card.
  const ctaPriceLabel = billingInterval === 'year' ? annualLabel : priceLabel;
  const sourceCategory = getSourceCategory(source);
  const quotaWall = /limit|quota/.test(source);
  const primaryOffer = getPrimaryPricingOffer(source, zhPayment);
  const dayPassFirst = primaryOffer === 'day_pass';
  const modalCopy = getSourceCopy(strings, sourceCategory);
  const orderedFeatureClusters = getOrderedFeatureClusters(strings, sourceCategory);
  // Eligibility is checked against authenticated Stripe customer history.
  // A day-pass record alone does not mean a subscription trial was used.
  const checkoutCopy = subscriptionCheckoutCopy(lang, pricingQuote?.trialEligible);
  const paymentCheckoutCopy = subscriptionCheckoutCopy(zhPayment ? 'zh' : lang, pricingQuote?.trialEligible);
  const ctaLabel = checkoutCopy.cta;
  const paymentCtaLabel = paymentCheckoutCopy.cta;
  const paymentCtaNote = `${paymentCheckoutCopy.note} ${paymentStrings.renewalNote}`;
  // ja/en (and any non-zh-payment visitor): one-time credit packs render as a
  // secondary option below the subscription CTA (zh-payment visitors keep
  // their own primary-packs layout below, untouched).
  const showOneTimePurchases = needsRecovery || plan !== 'pro';
  const showSecondaryCreditPacks = !zhPayment && showOneTimePurchases;

  useEffect(() => {
    if (!isOpen) {
      ctaShownKeyRef.current = '';
      return;
    }
    if (needsRecovery) return;
    const key = `${source}:${currency}:${lang}:${Boolean(user)}`;
    if (ctaShownKeyRef.current === key) return;
    ctaShownKeyRef.current = key;
    trackEvent('pricing_modal_cta_shown', {
      plan: 'pro',
      currency,
      source,
      lang,
      logged_in: Boolean(user),
      page_path: window.location.pathname,
      primary_offer: primaryOffer,
      layout_version: 'pro_first_v2',
      ...(zhPayment ? { zh_payment_layout: true } : {}),
    });
  }, [isOpen, source, currency, lang, user, zhPayment, primaryOffer, needsRecovery]);

  useEffect(() => {
    if (!isOpen) {
      packsShownKeyRef.current = '';
      return;
    }
    if (!showSecondaryCreditPacks) return;
    const key = `${source}:${lang}`;
    if (packsShownKeyRef.current === key) return;
    packsShownKeyRef.current = key;
    trackEvent('credit_packs_viewed', { lang });
  }, [isOpen, showSecondaryCreditPacks, source, lang]);

  // EARLYBIRD exposure: count opens across sessions; show from the 2nd open.
  useEffect(() => {
    if (!isOpen) {
      earlybirdFiredRef.current = false;
      setShowEarlybird(false);
      return;
    }
    if (needsRecovery || plan === 'pro' || zhPayment) return; // packs/pass flows have no promo field
    let opens = 1;
    try {
      opens = Number(localStorage.getItem('la_paywall_opens') || '0') + 1;
      localStorage.setItem('la_paywall_opens', String(opens));
    } catch { /* storage unavailable — keep banner hidden */ }
    // Quota walls skip the 2-open warmup: the person is blocked mid-task
    // RIGHT NOW, which is exactly when a first-purchase discount converts.
    // First tool-success moment (tool_outcome:*) also skips the warmup: both
    // paying cohorts converted right after experiencing a result, so that is
    // where the strongest offer belongs — not on the second visit.
    const firstSuccessMoment = /^tool_outcome:/.test(source);
    if (opens >= 2 || quotaWall || firstSuccessMoment) {
      setShowEarlybird(true);
      if (!earlybirdFiredRef.current) {
        earlybirdFiredRef.current = true;
        trackEvent('earlybird_shown', {
          surface: quotaWall ? 'quota_wall' : firstSuccessMoment ? 'tool_outcome' : 'pricing_modal_repeat',
          opens, source, lang,
        });
      }
    }
  }, [isOpen, plan, zhPayment, source, lang, quotaWall, needsRecovery]);

  // Bundle-value reframe view — fires once per modal open (not per
  // source/currency combo like pricing_modal_cta_shown above) so we can
  // compare conversion under the bundle framing vs. the old flat feature
  // list, independent of which source opened the modal.
  useEffect(() => {
    if (!isOpen) {
      bundleViewFiredRef.current = false;
      return;
    }
    if (needsRecovery || bundleViewFiredRef.current) return;
    bundleViewFiredRef.current = true;
    trackEvent('pricing_bundle_view', {
      source,
      lang,
      currency,
      page_path: window.location.pathname,
    });
  }, [isOpen, source, lang, currency, needsRecovery]);

  // The annual plan has existed since launch and has never been chosen once
  // (46/46 subscriptions monthly). Untracked, we cannot tell whether nobody
  // wants it or nobody notices the toggle, so record the switch itself before
  // deciding anything about defaults or pricing.
  const selectBillingInterval = useCallback((interval: 'month' | 'year') => {
    setBillingInterval(interval);
    trackEvent('billing_interval_selected', {
      interval,
      source,
      currency,
      lang,
      one_shot_layout: dayPassFirst,
      page_path: window.location.pathname,
    });
  }, [source, currency, lang, dayPassFirst]);

  useEffect(() => { if (!isOpen) clearCheckout(); }, [isOpen, clearCheckout]);

  useEffect(() => {
    clearCheckout();
    setCheckoutError(null);
    setIsRedirecting(false);
  }, [clearCheckout, needsRecovery, user?.id]);

  useEffect(() => { clearCheckout(); }, [clearCheckout, currency, billingInterval, source]);

  const handleUpgrade = useCallback(async (emailArg?: unknown) => {
    if (isCurrentUserBillingRecoveryRequired()) {
      await recovery.openBilling();
      return;
    }
    const email = typeof emailArg === 'string' ? emailArg : undefined;
    // Email gate removed 2026-08-04: it zeroed begin_checkout for two days
    // (38-44 modal views/day, 0 CTA follow-through). Stripe-native abandoned
    // checkout recovery (after_expiration.recovery) replaces the capture.
    trackEvent('upgrade_button_clicked', {
      plan: 'pro',
      currency,
      source,
      lang,
      logged_in: Boolean(user),
      page_path: window.location.pathname,
    });
    trackEvent('begin_checkout', {
      plan: 'pro',
      currency,
      interval: billingInterval,
      source,
      lang,
      logged_in: Boolean(user),
      page_path: window.location.pathname,
      ...(zhPayment ? { zh_payment_layout: true } : {}),
    });
    setCheckoutError(null);
    clearCheckout();
    setIsRedirecting(true);
    // Prefill the coupon the modal/banner promised — manual promo-code entry
    // on the Stripe page had a 0% redemption rate. The win-back banner
    // advertises EARLYBIRD even when the modal's own coupon block stays
    // hidden (la_paywall_opens < 2), so include that source explicitly.
    // pass_expiry wins over EARLYBIRD: its banner promised DAYPASS40.
    const promo = source === 'pass_expiry'
      ? 'DAYPASS40'
      : showEarlybird || source === 'winback_banner'
      ? 'EARLYBIRD'
      : undefined;
    const checkout = await createCheckoutSession(currency, source, billingInterval, promo, email);
    if (checkout) {
      if (checkout.kind !== 'billing_management') trackEvent('checkout_session_created', {
        plan: 'pro',
        currency,
        interval: billingInterval,
        source,
        page_path: window.location.pathname,
      });
      navigateCheckout(checkout, {
        plan: 'pro',
        currency,
        source,
        page_path: window.location.pathname,
      });
      if (checkout.preserveInNewTab) setIsRedirecting(false);
    } else {
      setIsRedirecting(false);
      trackEvent('checkout_session_failed', {
        plan: 'pro',
        currency,
        source,
        page_path: window.location.pathname,
      });
      setCheckoutError(paymentStrings.checkoutFailed);
    }
  }, [navigateCheckout, clearCheckout, user, createCheckoutSession, currency, source, lang, strings, paymentStrings, zhPayment, billingInterval, showEarlybird, recovery.openBilling]);

  // Pro Passes (one-time, JPY-only prices; WeChat Pay appears automatically
  // because the checkout runs in payment mode). 'month' = 30-day ¥980,
  // 'day' = 24-hour ¥200 (GA plan 'day_pass' so funnels stay separable).
  const handleBuyPass = useCallback(async (pass: 'day' | 'month', emailArg?: unknown) => {
    recovery.pause();
    const email = typeof emailArg === 'string' ? emailArg : undefined;
    // Email gate removed 2026-08-04: it zeroed begin_checkout for two days
    // (38-44 modal views/day, 0 CTA follow-through). Stripe-native abandoned
    // checkout recovery (after_expiration.recovery) replaces the capture.
    const gaPlan = pass === 'day' ? 'day_pass' : 'pro_pass';
    trackEvent('begin_checkout', {
      plan: gaPlan,
      currency: 'jpy',
      source,
      lang,
      logged_in: Boolean(user),
      page_path: window.location.pathname,
      ...(zhPayment ? { zh_payment_layout: true } : {}),
    });
    setCheckoutError(null);
    clearCheckout();
    setIsPassRedirecting(true);
    const checkout = await createPassCheckout(pass, email, source);
    if (checkout) {
      navigateCheckout(checkout, {
        plan: gaPlan,
        currency: 'jpy',
        source,
        page_path: window.location.pathname,
      });
      if (checkout.preserveInNewTab) setIsPassRedirecting(false);
    } else {
      setIsPassRedirecting(false);
      trackEvent('checkout_session_failed', {
        plan: gaPlan,
        currency: 'jpy',
        source,
        page_path: window.location.pathname,
      });
      setCheckoutError(paymentStrings.checkoutFailed);
    }
  }, [navigateCheckout, clearCheckout, user, createPassCheckout, source, lang, strings, paymentStrings, zhPayment, recovery.pause]);

  const handleBuyCredits = useCallback(async (packId: string, emailArg?: unknown) => {
    recovery.pause();
    const email = typeof emailArg === 'string' ? emailArg : undefined;
    // Email gate removed 2026-08-04: it zeroed begin_checkout for two days
    // (38-44 modal views/day, 0 CTA follow-through). Stripe-native abandoned
    // checkout recovery (after_expiration.recovery) replaces the capture.
    trackEvent('begin_checkout', {
      plan: 'credits',
      pack_id: packId,
      currency,
      source,
      lang,
      logged_in: Boolean(user),
      page_path: window.location.pathname,
      ...(zhPayment ? { zh_payment_layout: true } : {}),
    });
    setCheckoutError(null);
    clearCheckout();
    setBuyingPackId(packId);
    const checkout = await buyCredits(packId, currency, email, source);
    if (checkout) {
      navigateCheckout(checkout, {
        plan: 'credits',
        pack_id: packId,
        currency,
        source,
        page_path: window.location.pathname,
      });
      if (checkout.preserveInNewTab) setBuyingPackId(null);
    } else {
      setBuyingPackId(null);
      trackEvent('checkout_session_failed', {
        plan: 'credits',
        pack_id: packId,
        currency,
        source,
        page_path: window.location.pathname,
      });
      setCheckoutError(paymentStrings.checkoutFailed);
    }
  }, [navigateCheckout, clearCheckout, user, buyCredits, currency, source, lang, strings, paymentStrings, zhPayment, recovery.pause]);


  return (
    <Modal isOpen={isOpen} onClose={() => { recovery.pause(); onClose(); }} title={needsRecovery ? billingRecoveryCopy(recoveryLang).title : modalCopy.title} size="medium" className="pricing-modal-dialog">
      <div className="pricing-modal" data-primary-offer={needsRecovery ? 'billing_recovery' : primaryOffer} data-pricing-layout="pro_first_v2">
        <CheckoutContinuation pending={pendingCheckout} lang={lang} />
        {checkoutError && (
          <p className="pricing-modal__error" role="alert">{checkoutError}</p>
        )}

        {needsRecovery && <BillingRecoveryPanel lang={recoveryLang} action={recovery} />}

        {needsRecovery && !zhPayment && (
          <section className="pricing-modal__offer" aria-label={paymentStrings.oneTimeOptions}>
            <h3>{paymentStrings.oneTimeOptions}</h3>
            <div className="pricing-modal__secondary-action" data-purchase-offer="day_pass">
              <button type="button" className="pricing-modal__secondary-button" onClick={() => handleBuyPass('day')} disabled={isPassRedirecting || buyingPackId !== null}>
                {isPassRedirecting ? paymentStrings.redirecting : dayPassCta}
              </button>
              <span className="pricing-modal__secondary-note">{paymentStrings.dayPassNote}</span>
            </div>
            <div className="pricing-modal__secondary-action" data-purchase-offer="pro_pass">
              <button type="button" className="pricing-modal__secondary-button" onClick={() => handleBuyPass('month')} disabled={isPassRedirecting || buyingPackId !== null}>
                {isPassRedirecting ? paymentStrings.redirecting : paymentStrings.passCta}
              </button>
              <span className="pricing-modal__secondary-note">{paymentStrings.passNote}</span>
            </div>
            <p className="pricing-modal__refund-note">{paymentStrings.refundNote}</p>
          </section>
        )}

        {!needsRecovery && !zhPayment && plan !== 'pro' && (
          <section className="pricing-modal__offer" aria-label="Pro">
            <div className="pricing-modal__offer-heading">
              <strong>LA Studio Pro</strong>
              <span className={`pricing-modal__offer-price${!(billingInterval === 'year' ? pricingQuote?.yearly : pricingQuote?.monthly) ? ' pricing-modal__offer-price--pending' : ''}`} data-testid="pro-offer-price">{ctaPriceLabel}</span>
            </div>
            <div className="pricing-modal__billing-toggle" role="group" aria-label={lang === 'ja' ? '請求期間' : 'Billing interval'}>
              <button
                type="button"
                className={`pricing-modal__billing-option${billingInterval === 'month' ? ' is-active' : ''}`}
                aria-pressed={billingInterval === 'month'}
                onClick={() => selectBillingInterval('month')}
              >
                {paymentStrings.billingMonthly} {pricingQuote?.monthly ? priceLabel : ''}
              </button>
              <button
                type="button"
                className={`pricing-modal__billing-option${billingInterval === 'year' ? ' is-active' : ''}`}
                aria-pressed={billingInterval === 'year'}
                onClick={() => selectBillingInterval('year')}
              >
                {paymentStrings.billingAnnual} {pricingQuote?.yearly ? annualLabel : ''}
                {annualBadge && <span className="pricing-modal__billing-badge">{annualBadge}</span>}
              </button>
            </div>
            {dayPassFirst && (
              <div className="pricing-modal__primary-action" data-purchase-offer="day_pass">
                <button
                  type="button"
                  className="pricing-modal__primary-button"
                  onClick={() => handleBuyPass('day')}
                  disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
                >
                  {isPassRedirecting ? paymentStrings.redirecting : dayPassCta}
                </button>
                <span className="pricing-modal__primary-note">{paymentStrings.dayPassNote}</span>
              </div>
            )}
            <div className={`pricing-modal__${dayPassFirst ? 'secondary' : 'primary'}-action`} data-purchase-offer="subscription">
              <button
                type="button"
                className={`pricing-modal__${dayPassFirst ? 'secondary' : 'primary'}-button`}
                data-testid="pro-subscription-cta"
                aria-describedby="pro-subscription-terms"
                onClick={handleUpgrade}
                disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
              >
                {isRedirecting ? strings.redirecting : paymentCtaLabel}
              </button>
              <span className={`pricing-modal__${dayPassFirst ? 'secondary' : 'primary'}-note`} id="pro-subscription-terms" data-testid="pro-subscription-terms">{paymentCtaNote}</span>
            </div>
            {showEarlybird && billingInterval === 'month' && source !== 'pass_expiry' && !(pricingQuote?.ppp && !pricingQuote?.monthly?.regional) && (
              <div className="pricing-modal__earlybird">
                <strong>{paymentStrings.earlybirdTitle}</strong>
                <span>{paymentStrings.earlybirdBody}</span>
              </div>
            )}
            <details className="pricing-modal__alternatives">
              <summary>{paymentStrings.oneTimeOptions}</summary>
              {!dayPassFirst && (
                <div className="pricing-modal__secondary-action" data-purchase-offer="day_pass">
                  <button
                    type="button"
                    className="pricing-modal__secondary-button"
                    onClick={() => handleBuyPass('day')}
                    disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
                  >
                    {isPassRedirecting ? paymentStrings.redirecting : dayPassCta}
                  </button>
                  <span className="pricing-modal__secondary-note">{paymentStrings.dayPassNote}</span>
                </div>
              )}
              <div className="pricing-modal__secondary-action" data-purchase-offer="pro_pass">
                <button
                  type="button"
                  className="pricing-modal__secondary-button"
                  onClick={() => handleBuyPass('month')}
                  disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
                >
                  {isPassRedirecting ? paymentStrings.redirecting : paymentStrings.passCta}
                </button>
                <span className="pricing-modal__secondary-note">{paymentStrings.passNote}</span>
              </div>
            </details>
            <p className="pricing-modal__refund-note">{paymentStrings.refundNote}</p>
          </section>
        )}

        {/* zh-payment visitors (zh page OR zh browser language): the one-time
            Pro 30-Day Pass is the primary CTA. Stripe subscription mode only
            offers card/link — a dead end for most Chinese buyers — while
            WeChat Pay works for one-time (mode=payment) checkouts, which is
            exactly what the pass uses. Copy renders in zh even when the page
            chrome around it is EN/ja, since a zh-browser visitor reads
            Chinese — that's the whole point of this branch. */}
        {zhPayment && showOneTimePurchases && (() => {
          // Quota wall: the ¥200/24h pass matches the "just this one song"
          // moment and leads; otherwise the 30-day pass stays primary.
          const dayPassButton = (variant: 'primary' | 'secondary') => (
            <div className={`pricing-modal__${variant}-action`}>
              <button
                type="button"
                className={`pricing-modal__${variant}-button`}
                onClick={() => handleBuyPass('day')}
                disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
              >
                {isPassRedirecting ? paymentStrings.redirecting : (
                  <>
                    {dayPassCta}
                    {pricingQuote?.dayPass?.currency === 'jpy' && <span className="pricing-modal__cny-hint">{` ${formatCnyHint(DAY_PASS_PRICE_JPY)}`}</span>}
                  </>
                )}
              </button>
              <span className={`pricing-modal__${variant}-note`}>{paymentStrings.dayPassNote}</span>
            </div>
          );
          const monthPassButton = (variant: 'primary' | 'secondary') => (
            <div className={`pricing-modal__${variant}-action`}>
              <button
                type="button"
                className={`pricing-modal__${variant}-button`}
                onClick={() => handleBuyPass('month')}
                disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
              >
                {isPassRedirecting ? paymentStrings.redirecting : (
                  <>
                    {paymentStrings.passCta}
                    <span className="pricing-modal__cny-hint">{` ${formatCnyHint(PRO_PASS_PRICE_JPY)}`}</span>
                  </>
                )}
              </button>
              <span className={`pricing-modal__${variant}-note`}>{paymentStrings.passNote}</span>
            </div>
          );
          // Day pass leads unconditionally for zh-payment visitors: the ¥980
          // pass as primary produced only abandoned sessions (3 opens, 0
          // completions 07-28〜30); ¥200 (~10元) is the impulse-priced door.
          return <>{dayPassButton('primary')}{monthPassButton('secondary')}</>;
        })()}

        {/* zh-payment visitors: one-time credit packs stay as the second
            option (also WeChat-Pay-capable, for per-render Neural HQ use). */}
        {zhPayment && showOneTimePurchases && (
          <div className="pricing-modal__credit-packs">
            <div className="pricing-modal__credit-packs-header">
              <h4>{paymentStrings.creditPacksTitle}</h4>
              <span>{paymentStrings.creditPacksNote}</span>
            </div>
            <div className="pricing-modal__credit-pack-list">
              {CREDIT_PACKS.map((pack) => {
                const price = currency === 'jpy' ? `¥${pack.priceJpy.toLocaleString()}` : `$${pack.priceUsd}`;
                const cnyHint = currency === 'jpy' ? formatCnyHint(pack.priceJpy) : '';
                return (
                  <button
                    key={pack.id}
                    type="button"
                    className="pricing-modal__credit-pack"
                    onClick={() => handleBuyCredits(pack.id)}
                    disabled={buyingPackId !== null || isRedirecting || isPassRedirecting}
                  >
                    <span className="pricing-modal__credit-pack-credits">{pack.credits} {paymentStrings.creditsUnit}</span>
                    <span className="pricing-modal__credit-pack-price">
                      {price}
                      {cnyHint && <span className="pricing-modal__cny-hint">{` ${cnyHint}`}</span>}
                    </span>
                    <span className="pricing-modal__credit-pack-buy">
                      {buyingPackId === pack.id ? paymentStrings.redirecting : paymentStrings.buyCredits}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {zhPayment && showOneTimePurchases && (
          <p className="pricing-modal__refund-note">{paymentStrings.refundNote}</p>
        )}


        {/* Detailed benefits follow the purchase summary so mobile visitors
            can see the offer and its terms without scrolling past them. */}
        {!needsRecovery && plan !== 'pro' && (
          <div className="pricing-modal__bundle-banner">
            <p className="pricing-modal__bundle-header">{strings.bundleHeader(pricingQuote?.monthly ? priceLabel : '')}</p>
            <p className="pricing-modal__bundle-anchor">{strings.bundleAnchor(priceLabel)}</p>
          </div>
        )}
        {!needsRecovery && <>
        <p className="pricing-modal__context">{modalCopy.subtitle}</p>
        <ul className="pricing-modal__highlights">
          {modalCopy.highlights.map((highlight) => (
            <li key={highlight}>{highlight}</li>
          ))}
        </ul>
        </>}

        {/* Non-zh-payment visitors: one-time credit packs as a secondary
            option below the subscription CTA — same packs zh-payment
            visitors get primarily, just framed as an alternative here
            instead of the lead action. */}
        {showSecondaryCreditPacks && (
          <div className="pricing-modal__credit-packs pricing-modal__credit-packs--secondary">
            <div className="pricing-modal__credit-packs-header">
              <h4>{paymentStrings.creditPacksTitle}</h4>
              <span>{paymentStrings.creditPacksSecondaryNote}</span>
            </div>
            <div className="pricing-modal__credit-pack-list">
              {CREDIT_PACKS.map((pack) => {
                const price = currency === 'jpy' ? `¥${pack.priceJpy.toLocaleString()}` : `$${pack.priceUsd}`;
                const minutes = Math.round(pack.credits * (NEURAL_HQ_SECONDS_PER_CREDIT / 60));
                return (
                  <button
                    key={pack.id}
                    type="button"
                    className="pricing-modal__credit-pack"
                    onClick={() => handleBuyCredits(pack.id)}
                    disabled={buyingPackId !== null || isRedirecting || isPassRedirecting}
                  >
                    <span className="pricing-modal__credit-pack-credits">{pack.credits} {paymentStrings.creditsUnit}</span>
                    <span className="pricing-modal__credit-pack-price">{price}</span>
                    <span className="pricing-modal__credit-pack-minutes">{paymentStrings.creditPacksMinutesLabel(minutes)}</span>
                    <span className="pricing-modal__credit-pack-buy">
                      {buyingPackId === pack.id ? paymentStrings.redirecting : paymentStrings.buyCredits}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* zh-payment visitors: subscription trial is demoted to the third
            option, labeled with its card-only limitation. */}
        {!needsRecovery && zhPayment && plan !== 'pro' && (
          <div className="pricing-modal__secondary-action">
            <button
              type="button"
              className="pricing-modal__secondary-button"
              onClick={handleUpgrade}
              disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
            >
              {isRedirecting ? paymentStrings.redirecting : paymentCtaLabel}
            </button>
            <span className="pricing-modal__secondary-note">
              {paymentCtaNote} {ctaPriceLabel}
              {paymentStrings.subscriptionCardOnly ? `${paymentStrings.subscriptionCardOnly}` : ''}
            </span>
          </div>
        )}

        {/* Currency toggle */}
        {!needsRecovery && pricingQuote?.monthly && (pricingQuote.monthly.regional || pricingQuote.monthly.currency !== currency) && (
          <p className="pricing-modal__secondary-note">
            {lang === 'ja' ? `Proの請求通貨は${pricingQuote.monthly.currency.toUpperCase()}です。以下はクレジットパックの通貨です。`
              : lang === 'zh' ? `Pro 以 ${pricingQuote.monthly.currency.toUpperCase()} 计费。以下选项用于积分包币种。`
              : `Pro is billed in ${pricingQuote.monthly.currency.toUpperCase()}. Choose the credit-pack currency below.`}
          </p>
        )}
        <div className="pricing-currency-toggle">
          <button
            className={currency === 'jpy' ? 'active' : ''}
            onClick={() => setCurrency('jpy')}
          >
            JPY (¥)
          </button>
          <button
            className={currency === 'usd' ? 'active' : ''}
            onClick={() => setCurrency('usd')}
          >
            USD ($)
          </button>
        </div>


        {!needsRecovery && <div className="pricing-cards">
          {/* Free plan */}
          <div className={`pricing-card ${plan === 'free' ? 'pricing-card--current' : ''}`}>
            <div className="pricing-card-header">
              <h3>{strings.free}</h3>
              <div className="pricing-card-price">{currency === 'jpy' ? '¥0' : '$0'}</div>
            </div>
            <ul className="pricing-card-features">
              {strings.freeFeatures.map((f) => (
                <li key={f.text} className={f.disabled ? 'pricing-feature--disabled' : undefined}>{f.text}</li>
              ))}
            </ul>
            {plan === 'free' && (
              <div className="pricing-card-badge">{strings.currentPlan}</div>
            )}
          </div>

          {/* Pro plan */}
          <div className={`pricing-card pricing-card--pro ${plan === 'pro' ? 'pricing-card--current' : ''}`}>
            <div className="pricing-card-header">
              <h3>Pro</h3>
              <div className="pricing-card-price">{ctaPriceLabel}</div>
            </div>
            <div className="pricing-card-feature-groups">
              {orderedFeatureClusters.map((cluster) => (
                <div key={cluster.id} className="pricing-card-feature-group">
                  <div className="pricing-card-feature-group__label">{cluster.label}</div>
                  <ul className="pricing-card-features">
                    {cluster.features.map((f) => (
                      <li key={f} className="pricing-feature--highlight">{f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {dayPassFirst && plan !== 'pro' && (
              <p className="pricing-modal__continuity-note">{checkoutCopy.cta}</p>
            )}
            {plan === 'pro' ? (
              <div className="pricing-card-badge">{strings.currentPlan}</div>
            ) : (
              <>
                <button
                  className="pricing-upgrade-btn"
                  onClick={handleUpgrade}
                  disabled={isRedirecting || isPassRedirecting || buyingPackId !== null}
                >
                  {isRedirecting ? strings.redirecting : ctaLabel}
                </button>
                {/* zh-payment: this is the LAST unlabeled subscription entry —
                    the top one (line ~1137) already carries the card-only
                    warning. 28d Stripe: 182 of 214 subscription sessions
                    expired untouched; don't let WeChat/Alipay users walk into
                    a card-only page unwarned from here either. */}
                {zhPayment && (
                  <span className="pricing-modal__secondary-note">
                    {paymentStrings.subscriptionCardOnly}
                  </span>
                )}
              </>
            )}
          </div>
        </div>}
      </div>
    </Modal>
  );
};

export default PricingModal;
